import React, { useState, useRef, useEffect } from 'react';
import { Grid, FormControl, TextField, MenuItem, Button, FormHelperText, Autocomplete, Chip, Box } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PropTypes from 'prop-types';
import muiInputStyles from '../MuiInput/MuiInputStyles';
import { parseISO, format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';

function TableFilterWorkflowRuns(props) {
    const classes = muiInputStyles();
    const optionsArray = props.tableDataKeyList;
    // Add local error state for start/end date
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');
    
    // Track focus state and calculate visible tags when unfocused
    const [isFocused, setIsFocused] = useState(false);
    const [visibleTagCount, setVisibleTagCount] = useState(5);
    const inputRef = useRef(null);

    // Validation helpers
    const minDate = new Date(2020, 0, 1);
    const maxDate = new Date();

    const handleCustomStartDateChange = (newValue) => {
        props.setCustomStartDate(newValue); // Always set, even if invalid
        if (!newValue) {
            setStartDateError('');
            props.setCustomStartDate(null);
            return;
        }
        if (!isValid(newValue)) {
            setStartDateError('Please enter a valid date.');
            props.setCustomStartDate(null);
            return;
        }
        if (newValue < minDate) {
            setStartDateError('Date cannot be before 01/01/2020.');
            props.setCustomStartDate(null);
            return;
        }
        if (newValue > maxDate) {
            setStartDateError('Date cannot be after today.');
            props.setCustomStartDate(null);
            return;
        }
        // Cross-field validation
        if (props.customEndDate && isValid(props.customEndDate)) {
            if (newValue > props.customEndDate) {
                setStartDateError('Start date cannot be after end date.');
                return;
            }
        }
        setStartDateError('');
        // Only clear end date error if end date is valid on its own
        if (props.customEndDate && isValid(props.customEndDate)) {
            if (
                props.customEndDate >= minDate &&
                props.customEndDate <= maxDate &&
                props.customEndDate >= newValue
            ) {
                setEndDateError('');
            }
        }
        props.setCustomStartDate(newValue);
    };

    const handleCustomEndDateChange = (newValue) => {
        props.setCustomEndDate(newValue); // Always set, even if invalid
        if (!newValue) {
            setEndDateError('');
            props.setCustomEndDate(null);
            return;
        }
        if (!isValid(newValue)) {
            setEndDateError('Please enter a valid date.');
            props.setCustomEndDate(null);
            return;
        }
        if (newValue < minDate) {
            setEndDateError('Date cannot be before 01/01/2020.');
            props.setCustomEndDate(null);
            return;
        }
        if (newValue > maxDate) {
            setEndDateError('Date cannot be after today.');
            props.setCustomEndDate(null);
            return;
        }
        // Cross-field validation
        if (props.customStartDate && isValid(props.customStartDate)) {
            if (newValue < props.customStartDate) {
                setEndDateError('End date cannot be before start date.');
                return;
            }
        }
        setEndDateError('');

        // Only clear start date error if start date is valid on its own
        if (props.customStartDate && isValid(props.customStartDate)) {
            if (
                props.customStartDate >= minDate &&
                props.customStartDate <= maxDate &&
                props.customStartDate <= newValue
            ) {
                setStartDateError('');
            }
        }
        props.setCustomEndDate(newValue);
    };

    const handleDaysRangeChange = (event) => {
        const selected = event.target.value;
        if (selected !== "Custom Range") {
            setStartDateError('');
            setEndDateError('');
        }
        props.handleDaysRangeChange(event);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const parsedDate = parseISO(dateString);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localDate = utcToZonedTime(parsedDate, timeZone);
        return isValid(localDate) ? format(localDate, 'MM/dd/yyyy') : dateString;
    };

    // Unique, sorted filter values for non-date columns
    const uniqueFormattedValues = [
        ...new Set(
            props.filterOptions
                .map(option => option ? formatDate(option) : null)
                .filter(date => date)
        )
    ];
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const sortedUniqueFormattedValues = [...uniqueFormattedValues].sort((a, b) => {
        if (["workflowRunStartDate", "workflowRunEndDate"].includes(props.searchFilter)) {
            const dateA = utcToZonedTime(parseISO(a), timeZone);
            const dateB = utcToZonedTime(parseISO(b), timeZone);
            return dateA - dateB;
        } else {
            return a.localeCompare(b);
        }
    });

    // Calculate visible tags when not focused
    useEffect(() => {
        if (isFocused || !props.filterBy || props.filterBy.length === 0) {
            return;
        }

        const calculateVisibleTags = () => {
            if (!inputRef.current) return;

            const inputElement = inputRef.current.querySelector('.MuiInputBase-root');
            if (!inputElement) return;

            const containerWidth = inputElement.offsetWidth;
            // Reserve space for padding, clear icon, dropdown icon, and margins
            const reservedSpace = 100;
            const availableWidth = containerWidth - reservedSpace;
            
            const values = Array.isArray(props.filterBy) ? props.filterBy : [];
            let totalWidth = 0;
            let count = 0;
            
            // Spacing and sizing constants (approximate MUI chip sizing)
            const chipGap = 4;
            const avgCharWidth = 8.5; // Approximate character width in chips
            const chipBasePadding = 32; // Base padding + delete icon
            const moreChipWidth = 50; // Width for "+N" chip

            for (let i = 0; i < values.length; i++) {
                const chipWidth = (values[i].length * avgCharWidth) + chipBasePadding + chipGap;
                
                // Check if we can fit this chip plus potentially the "+N" chip
                const needsMoreChip = i < values.length - 1;
                const requiredWidth = totalWidth + chipWidth + (needsMoreChip ? moreChipWidth : 0);
                
                if (requiredWidth > availableWidth && count > 0) {
                    break;
                }
                
                totalWidth += chipWidth;
                count++;
            }

            setVisibleTagCount(Math.max(1, count));
        };

        // Use requestAnimationFrame to ensure DOM is ready
        const frame = requestAnimationFrame(() => {
            calculateVisibleTags();
        });

        return () => cancelAnimationFrame(frame);
    }, [props.filterBy, isFocused]);

    // Helper: is the date range custom?
    const isCustomRange = props.selectedTimeRange === 'Custom Range';

    return (
        <Grid container spacing={3}>
            {/* Row 1: Date Range and Clear Filters */}
            <Grid item xs={12}>
                <Grid container spacing={3}>
                    <Grid item sm={4} xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            id="daysRangeFilter"
                            className={classes.muiInputs}
                            value={props.selectedTimeRange}
                            onChange={handleDaysRangeChange}
                            variant="outlined"
                            label="Date Range"
                            helperText={' '} // Add empty helper text to avoid shifting
                            select
                            SelectProps={{
                                MenuProps: { disablePortal: true },
                            }}
                        >
                            <MenuItem value="All Time">All Time</MenuItem>
                            <MenuItem value="1 Week">1 Week</MenuItem>
                            <MenuItem value="1 Month">1 Month</MenuItem>
                            <MenuItem value="6 Months">6 Months</MenuItem>
                            <MenuItem value="1 Year">1 Year</MenuItem>
                            <MenuItem value="2 Years">2 Years</MenuItem>
                            <MenuItem value="5 Years">5 Years</MenuItem>
                            <MenuItem value="Custom Range">Custom Range</MenuItem>
                        </TextField>
                    </Grid>
                    {isCustomRange && (
                        <>
                            <Grid item sm={3} xs={12} className="customDateRange">
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Start Date"
                                        value={props.customStartDate}
                                        onChange={handleCustomStartDateChange}
                                        minDate={minDate}
                                        maxDate={props.customEndDate || maxDate}
                                        renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    fullWidth
                                                    variant="outlined"
                                                    className={classes.muiInputs}
                                                    error={!!startDateError}
                                                    helperText={startDateError || ' '}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {props.customStartDate && (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            aria-label="Clear start date"
                                                                            onClick={() => {
                                                                                props.setCustomStartDate(null);
                                                                                setStartDateError('');
                                                                                // Only clear cross-field errors on end date, not single-field errors
                                                                                if (props.customEndDate) {
                                                                                    if (!isValid(new Date(props.customEndDate))) {
                                                                                        // Do not clear error if end date is invalid
                                                                                    } else if (new Date(props.customEndDate) < minDate) {
                                                                                        setEndDateError('Date cannot be before 01/01/2020.');
                                                                                    } else if (new Date(props.customEndDate) > maxDate) {
                                                                                        setEndDateError('Date cannot be after today.');
                                                                                    } else {
                                                                                        setEndDateError('');
                                                                                    }
                                                                                }
                                                                            }}
                                                                            edge="end"
                                                                            size="small"
                                                                            tabIndex={0}
                                                                        >
                                                                            <ClearIcon />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )}
                                                                {params.InputProps?.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                        )}
                                        PopperProps={{
                                            'aria-label': 'Date picker dialog for start date',
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item sm={3} xs={12} className="customDateRange">
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        value={props.customEndDate}
                                        onChange={handleCustomEndDateChange}
                                        minDate={props.customStartDate || minDate}
                                        maxDate={maxDate}
                                        renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    fullWidth
                                                    variant="outlined"
                                                    className={classes.muiInputs}
                                                    error={!!endDateError}
                                                    helperText={endDateError || ' '}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {props.customEndDate && (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            aria-label="Clear end date"
                                                                            onClick={() => {
                                                                                props.setCustomEndDate(null);
                                                                                setEndDateError('');
                                                                                // Only clear cross-field errors on start date, not single-field errors
                                                                                if (props.customStartDate) {
                                                                                    if (!isValid(new Date(props.customStartDate))) {
                                                                                        // Do not clear error if start date is invalid
                                                                                    } else if (new Date(props.customStartDate) < minDate) {
                                                                                        setStartDateError('Date cannot be before 01/01/2020.');
                                                                                    } else if (new Date(props.customStartDate) > maxDate) {
                                                                                        setStartDateError('Date cannot be after today.');
                                                                                    } else {
                                                                                        setStartDateError('');
                                                                                    }
                                                                                }
                                                                                // Do not set setStartDateError('') in the else branch!
                                                                            }}
                                                                            edge="end"
                                                                            size="small"
                                                                            tabIndex={0}
                                                                        >
                                                                            <ClearIcon />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )}
                                                                {params.InputProps?.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                        )}
                                        PopperProps={{
                                            'aria-label': 'Date picker dialog for end date',
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </>
                    )}
                    
                </Grid>
            </Grid>
            {/* Row 2: Search Filter and Value Filter */}
            <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item sm={4} xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                fullWidth
                                size="small"
                                id="searchFilter"
                                className={classes.muiInputs}
                                value={props.searchFilter}
                                onChange={props.onFilterTypeChange}
                                variant="outlined"
                                label="Search Filter"
                                select
                                SelectProps={{
                                    MenuProps: { disablePortal: true },
                                }}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {optionsArray
                                    .filter(option => props.filterNameConfig && props.filterNameConfig.hasOwnProperty(option))
                                    .sort((a, b) => props.filterNameConfig[a].localeCompare(props.filterNameConfig[b]))
                                    .map(option => (
                                        <MenuItem key={option} value={option}>
                                            {"By " + props.filterNameConfig[option]}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </FormControl>
                    </Grid>
                    {/* Always show value filter for non-date columns, regardless of date range selection */}
                    {props.searchFilter && !["workflowRunStartDate", "workflowRunEndDate"].includes(props.searchFilter) ? (
                        <Grid item sm={6}  xs={12}>
                            <FormControl fullWidth ref={inputRef}>
                                <Autocomplete
                                    size="small"
                                    className={classes.muiInputs}
                                    variant="outlined"
                                    multiple
                                    id="filterByValueFilter"
                                    options={sortedUniqueFormattedValues}
                                    value={Array.isArray(props.filterBy) ? props.filterBy : (props.filterBy ? [props.filterBy] : [])}
                                    onChange={(event, newValue) => {
                                        props.handleFilterChange({ target: { value: newValue } });
                                    }}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    renderTags={(value, getTagProps) => {
                                        // When focused, show all chips (default behavior)
                                        if (isFocused) {
                                            return value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={index}
                                                    label={option}
                                                    size="small"
                                                />
                                            ));
                                        }
                                        
                                        // When not focused, limit to one line
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap',
                                                    gap: 0.5,
                                                    overflow: 'hidden',
                                                    width: '100%',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {value.slice(0, visibleTagCount).map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={index}
                                                        label={option}
                                                        size="small"
                                                    />
                                                ))}
                                                {value.length > visibleTagCount && (
                                                    <Chip
                                                        size="small"
                                                        label={`+${value.length - visibleTagCount}`}
                                                        sx={{ cursor: 'default' }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={props.filterNameConfig ? props.filterNameConfig[props.searchFilter] : props.searchFilter}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    alignItems: 'center',
                                                    ...((!isFocused && props.filterBy?.length > 0) && {
                                                        height: '40px',
                                                        minHeight: '40px',
                                                        maxHeight: '40px'
                                                    })
                                                },
                                                '& .MuiInputBase-root .MuiAutocomplete-input': {
                                                    minWidth: isFocused ? '30px' : '0px'
                                                }
                                            }}
                                        />
                                    )}
                                    filterSelectedOptions
                                />
                            </FormControl>
                        </Grid>
                    ) : null}
                    <Grid item sm style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                        <Button
                            id="clearFilters"
                            aria-label="Clear table filters"
                            onClick={() => {
                            setStartDateError('');
                            setEndDateError('');
                            props.onClearFilters();
                            }}
                            startIcon={<ClearAllIcon />}
                            variant="outlined"
                            color="primary"
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

TableFilterWorkflowRuns.propTypes = {
    onClearFilters: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    filterBy: PropTypes.array.isRequired,
    searchFilter: PropTypes.string.isRequired,
    handleFilterChange: PropTypes.func.isRequired,
    onFilterTypeChange: PropTypes.func.isRequired,
    tableDataKeyList: PropTypes.array.isRequired,
    filterOptions: PropTypes.array.isRequired,
    filterNameConfig: PropTypes.object,
    daysRangeDefined: PropTypes.bool,
    daysRange: PropTypes.string,
    handleDaysRangeChange: PropTypes.func,
    selectedTimeRange: PropTypes.string,
    customStartDate: PropTypes.any,
    setCustomStartDate: PropTypes.func,
    customEndDate: PropTypes.any,
    setCustomEndDate: PropTypes.func,
};

export default TableFilterWorkflowRuns;